// Roteador mínimo, por CAMINHO de URL (history.pushState + popstate), sem dependência nova.
//
// Por que não react-router: o site tem uma dúzia de caminhos fixos, dois deles com um parâmetro
// (`:id`, `:token`). Isso cabe em ~100 linhas — casar um padrão, ouvir o "voltar" do navegador e
// interceptar o clique num link. Uma biblioteca de roteamento traria API, versões e conceitos
// (loaders, layouts aninhados) que aqui não pagam o peso. Se o site crescer para rotas aninhadas
// de verdade, trocar é localizado: só este arquivo e o App.tsx conhecem o roteamento.
//
// Diferente do bi.web (navegação por estado), aqui a URL É o estado: o link mandado a um colega e o
// F5 precisam cair na tela certa. Em produção o Caddy de admin.plugchef.com.br devolve o index.html
// para todo caminho que não é arquivo do dist/ nem da API (/admin/v1/*).
import { useEffect, useMemo, useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';

const assinantes = new Set<() => void>();

function avisar(): void {
  assinantes.forEach((fn) => fn());
}

if (typeof window !== 'undefined') window.addEventListener('popstate', avisar);

function assinar(fn: () => void): () => void {
  assinantes.add(fn);
  return () => {
    assinantes.delete(fn);
  };
}

/** `/equipe/` e `/equipe` são o mesmo lugar — sem isso a barra final viraria "página não encontrada". */
export function normalizar(caminho: string): string {
  const c = caminho.replace(/\/{2,}/g, '/');
  return c.length > 1 ? c.replace(/\/+$/, '') : c;
}

function lerLocal(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Vai para outro caminho do site.
 * `substituir` troca a entrada atual do histórico (redirecionamentos, filtros) em vez de empilhar —
 * senão o "voltar" do navegador cairia de novo no redirecionamento e prenderia o usuário.
 */
export function navegar(para: string, opcoes: { substituir?: boolean; estado?: Record<string, unknown> } = {}): void {
  if (para === lerLocal()) return;
  // Substituir mantém o estado da entrada (a marca de "janela aberta pela tela", abaixo): trocar um
  // filtro ou a versão vista não pode fazer o "fechar" perder o caminho de volta.
  if (opcoes.substituir) window.history.replaceState(opcoes.estado ?? window.history.state, '', para);
  else {
    window.history.pushState(opcoes.estado ?? null, '', para);
    window.scrollTo(0, 0);
  }
  avisar();
}

/**
 * Fecha o que foi aberto por cima de uma tela (a janela de um produto): se a entrada atual do
 * histórico tem a `marca` (posta por quem abriu, em `navegar(..., { estado })`), volta uma entrada — o
 * "voltar" do navegador e o "fechar" dão no mesmo lugar, sem entrada repetida. Sem a marca (link
 * direto, F5), vai para o `destino` no lugar da entrada atual.
 */
export function voltarOu(destino: string, marca: string): void {
  const estado = window.history.state as Record<string, unknown> | null;
  if (estado && estado[marca]) window.history.back();
  else navegar(destino, { substituir: true });
}

export interface Localizacao {
  caminho: string;
  busca: URLSearchParams;
  /** Caminho + busca, para montar o "voltar para" do login. */
  completo: string;
}

/** O caminho atual; re-renderiza a cada navegação (inclusive "voltar" do navegador). */
export function useLocalizacao(): Localizacao {
  const completo = useSyncExternalStore(assinar, lerLocal, () => '/');
  return useMemo(() => {
    const i = completo.indexOf('?');
    const caminho = normalizar(i >= 0 ? completo.slice(0, i) : completo);
    const busca = new URLSearchParams(i >= 0 ? completo.slice(i) : '');
    return { caminho, busca, completo };
  }, [completo]);
}

/**
 * Casa um padrão (`/contratos/:id`) com um caminho. Devolve os parâmetros ou `null`.
 * Parâmetro vazio não casa: `/contratos/` (normalizado para `/contratos`) é a lista.
 */
export function casar(padrao: string, caminho: string): Record<string, string> | null {
  const p = normalizar(padrao).split('/');
  const c = normalizar(caminho).split('/');
  if (p.length !== c.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const parte = p[i]!;
    const valor = c[i]!;
    if (parte.startsWith(':')) {
      if (!valor) return null;
      try {
        params[parte.slice(1)] = decodeURIComponent(valor);
      } catch {
        return null;
      }
    } else if (parte !== valor) return null;
  }
  return params;
}

/** Monta `caminho?a=1&b=2`, deixando de fora o que não tem valor. */
export function comBusca(caminho: string, params: Record<string, string | number | null | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `${caminho}?${s}` : caminho;
}

type PropsLink = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { para: string; children: ReactNode };

/**
 * Link interno. É um `<a href>` de verdade — Ctrl+clique, clique do meio e "copiar endereço do
 * link" funcionam como em qualquer site; só o clique simples é interceptado para não recarregar.
 */
export function Link({ para, onClick, children, ...resto }: PropsLink): JSX.Element {
  function aoClicar(e: MouseEvent<HTMLAnchorElement>): void {
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (resto.target && resto.target !== '_self') return;
    e.preventDefault();
    navegar(para);
  }
  return (
    <a href={para} onClick={aoClicar} {...resto}>
      {children}
    </a>
  );
}

/**
 * Filtros de lista guardados na própria URL (`?busca=&situacao=&pagina=`): o F5, o "voltar" e o
 * link mandado a um colega abrem a lista no mesmo recorte. Troca a entrada do histórico em vez de
 * empilhar — senão cada letra digitada na busca viraria um "voltar".
 */
export function useBuscaUrl(): [URLSearchParams, (mudancas: Record<string, string | number | null | undefined>) => void] {
  const { caminho, busca } = useLocalizacao();
  const definir = (mudancas: Record<string, string | number | null | undefined>): void => {
    const q = new URLSearchParams(window.location.search);
    for (const [k, v] of Object.entries(mudancas)) {
      if (v === undefined || v === null || v === '') q.delete(k);
      else q.set(k, String(v));
    }
    const s = q.toString();
    navegar(s ? `${caminho}?${s}` : caminho, { substituir: true });
  };
  return [busca, definir];
}

/** Redireciona ao montar (substituindo a entrada do histórico). */
export function Redirecionar({ para }: { para: string }): null {
  useEffect(() => {
    navegar(para, { substituir: true });
  }, [para]);
  return null;
}

/**
 * Destino seguro do "voltar para" depois do login: só um caminho interno da MESMA área. Sem essa
 * conferência, `?volta=https://site-falso` faria o login mandar a pessoa para fora (open redirect).
 */
export function destinoSeguro(volta: string | null, prefixoArea: (c: string) => boolean, padrao: string): string {
  if (!volta || !volta.startsWith('/') || volta.startsWith('//') || volta.includes('\\')) return padrao;
  const caminho = normalizar(volta.split('?')[0] ?? '');
  return prefixoArea(caminho) ? volta : padrao;
}
